import { ContentCopy, Email } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { type ReactNode } from "react";

import { useToast } from "~hooks/useToast";

const tCopyEmail = "Copy email";
const tCopiedToClipboard = "Email copied to clipboard";

export interface EmailWithCopyIconProps {
  email: string;
  /** Show as mailto: link. Default true */
  asLink?: boolean;
  /** Show email icon before address. Default true */
  showEmailIcon?: boolean;
  /** Extra actions after the copy button (e.g. sales compose). */
  trailingSlot?: ReactNode;
}

export const EmailWithCopyIcon = ({
  email,
  asLink = true,
  showEmailIcon = true,
  trailingSlot,
}: EmailWithCopyIconProps) => {
  const toast = useToast();

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email) {
      return;
    }
    try {
      await navigator.clipboard.writeText(email);
      toast.success(tCopiedToClipboard);
    } catch {
      toast.error("Failed to copy email");
    }
  };

  const emailContent = (
    <Box
      component={asLink ? "a" : "div"}
      href={asLink ? `mailto:${email}` : undefined}
      sx={{
        alignItems: "center",
        color: "primary.main",
        display: "flex",
        gap: 0.5,
        minWidth: 0,
        textDecoration: asLink ? "none" : undefined,
      }}
    >
      {showEmailIcon && <Email fontSize="small" sx={{ color: "inherit", flexShrink: 0 }} />}
      <Typography
        component="span"
        variant="body2"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {email}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        gap: 0.5,
      }}
    >
      {emailContent}
      <Tooltip title={tCopyEmail}>
        <IconButton aria-label={tCopyEmail} onClick={handleCopy} size="small" sx={{ flexShrink: 0 }}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      {trailingSlot}
    </Box>
  );
};
